import { COINS_DATA, Coin } from '../../data/coins';

export interface UserSetStats {
  set_id: string;
  title: string;
  subtitle?: string;
  category: string;
  header_type: string;
  sort_order: number;
  circ_total: number;
  circ_owned: number;
  circ_pct: number;
  rare_total: number;
  rare_owned: number;
  rare_pct: number;
  total_coins: number;
  owned_coins: number;
  is_circ_completed: boolean;
  is_rare_completed: boolean;
  is_fully_completed: boolean;
  circ_reward_amount?: number;
  is_circ_reward_claimed?: boolean;
  rare_reward_amount?: number;
  is_rare_reward_claimed?: boolean;
}

export function getDenominationFaceValue(denomination?: string): number {
  switch (denomination) {
    case '2euro': return 2.00;
    case '1euro': return 1.00;
    case '50cent': return 0.50;
    case '20cent': return 0.20;
    case '10cent': return 0.10;
    case '5cent': return 0.05;
    case '2cent': return 0.02;
    case '1cent': return 0.01;
    default: return 2.00;
  }
}

export function getCoinCircRewardValue(mintage: number, denomination?: string): number {
  const faceVal = getDenominationFaceValue(denomination);
  const factor = Math.max(0.25, faceVal / 2.00);

  if (mintage > 100_000 && mintage <= 500_000) return Number((64.00 * factor).toFixed(2));
  if (mintage > 500_000 && mintage <= 1_000_000) return Number((16.00 * factor).toFixed(2));
  if (mintage > 1_000_000 && mintage <= 5_000_000) return Number((4.00 * factor).toFixed(2));
  if (mintage > 5_000_000) return faceVal;
  return 0.00;
}

export function calculateSetCircReward(coins: Coin[]): number {
  return Number(
    coins
      .filter(c => c.mintage > 100_000)
      .reduce((sum, c) => sum + getCoinCircRewardValue(c.mintage, c.denomination), 0)
      .toFixed(2)
  );
}

export function getCoinRareRewardValue(mintage: number, denomination?: string): number {
  const faceVal = getDenominationFaceValue(denomination);
  const factor = Math.max(0.25, faceVal / 2.00);

  if (mintage === 0) return Number((256.00 * factor).toFixed(2));
  if (mintage > 0 && mintage <= 100_000) return Number((128.00 * factor).toFixed(2));
  return 0.00;
}

export function calculateSetRareReward(coins: Coin[]): number {
  return Number(
    coins
      .filter(c => c.mintage <= 100_000 || c.mintage === 0)
      .reduce((sum, c) => sum + getCoinRareRewardValue(c.mintage, c.denomination), 0)
      .toFixed(2)
  );
}

export interface SetSeriesColumn {
  label: string;
  sublabel?: string;
  coins: Coin[];
}

export interface SetSeriesItem {
  id: string;
  title: string;
  subtitle?: string;
  category: 'joint' | 'series' | 'daterun';
  headerType: 'country' | 'year';
  coins: Coin[];
  columns: SetSeriesColumn[];
  stats?: UserSetStats | null;
}

export interface AlbumStatsSummary {
  totalCoins: number;
  ownedCoins: number;
  percentage: number;
  standardTotal: number;
  standardOwned: number;
  commemorativeTotal: number;
  commemorativeOwned: number;
  totalValue: number;
}

export const DENOMINATION_TABS = [
  { key: '2euro', label: '2 Euros' },
  { key: '1euro', label: '1 Euro' },
  { key: '50cent', label: '50 Cents' },
  { key: '20cent', label: '20 Cents' },
  { key: '10cent', label: '10 Cents' },
  { key: '5cent', label: '5 Cents' },
  { key: '2cent', label: '2 Cents' },
  { key: '1cent', label: '1 Cent' }
] as const;

export type DenominationKey = typeof DENOMINATION_TABS[number]['key'];

// 1. Get unique sorted list of all countries for standard coins by denomination
export function getStandardCountries(denomination: string = '2euro'): string[] {
  const set = new Set<string>();
  for (const c of COINS_DATA) {
    const cDenom = c.denomination || '2euro';
    if (c.type === 'standard' && cDenom === denomination) {
      set.add(c.country);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// 2. Get unique sorted list of all countries in commemorative coins (always 2euro)
export function getCommemorativeCountries(): string[] {
  const set = new Set<string>();
  for (const c of COINS_DATA) {
    if (c.type === 'commemorative') {
      set.add(c.country);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

// 3. Year ranges by denomination
export function getStandardYears(denomination: string = '2euro'): number[] {
  const years = new Set<number>();
  for (const c of COINS_DATA) {
    const cDenom = c.denomination || '2euro';
    if (c.type === 'standard' && cDenom === denomination) {
      years.add(c.year);
    }
  }
  return Array.from(years).sort((a, b) => a - b);
}

export function getCommemorativeYears(): number[] {
  const years = new Set<number>();
  for (const c of COINS_DATA) {
    if (c.type === 'commemorative') {
      years.add(c.year);
    }
  }
  return Array.from(years).sort((a, b) => a - b);
}

// 4. Matrix mapping for fast cell lookups by denomination
export function buildStandardMatrix(denomination: string = '2euro'): Map<string, Map<number, Coin[]>> {
  const matrix = new Map<string, Map<number, Coin[]>>();
  for (const coin of COINS_DATA) {
    const cDenom = coin.denomination || '2euro';
    if (coin.type !== 'standard' || cDenom !== denomination) continue;
    if (!matrix.has(coin.country)) {
      matrix.set(coin.country, new Map());
    }
    const countryMap = matrix.get(coin.country)!;
    if (!countryMap.has(coin.year)) {
      countryMap.set(coin.year, []);
    }
    countryMap.get(coin.year)!.push(coin);
  }
  return matrix;
}

export function buildCommemorativeMatrix(): Map<string, Map<number, Coin[]>> {
  const matrix = new Map<string, Map<number, Coin[]>>();
  for (const coin of COINS_DATA) {
    if (coin.type !== 'commemorative') continue;
    if (!matrix.has(coin.country)) {
      matrix.set(coin.country, new Map());
    }
    const countryMap = matrix.get(coin.country)!;
    if (!countryMap.has(coin.year)) {
      countryMap.set(coin.year, []);
    }
    countryMap.get(coin.year)!.push(coin);
  }
  return matrix;
}

/**
 * Counts completed sets from backend set statistics
 */
export function getCompletedSetsCount(sets: SetSeriesItem[]): number {
  return sets.filter(s => s.stats?.is_circ_completed).length;
}



